import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useContext, useState } from 'react';
import type { JSX } from 'react';
import { handleDisconnect } from '../Utils/HandleDisconnect';
import Typography from '@mui/material/Typography';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import type { IndexedDBContextProps } from '../Contexts/IndexedDBContext';
import IndexedDBContext from '../Contexts/IndexedDBContext';
import type { ModeContextProps } from '../Contexts/ModeContext';
import ModeContext from '../Contexts/ModeContext';
import type { SystemContextProps } from '../Contexts/SystemContext';
import SystemContext from '../Contexts/SystemContext';
import type { ApiContextProps } from '../Contexts/ApiContext';
import ApiContext from '../Contexts/ApiContext';
import type { TokenContextProps } from '../Contexts/TokenContext';
import TokenContext from '../Contexts/TokenContext';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import { useTitle } from 'shouldve-been-react';
import { LoadingButton, ConfirmDialog } from 'shouldve-been-mui';

type ErrorPageProps = {
    errorMessage: string,
    canDisconnect: boolean,
};

function ErrorPage(props: Readonly<ErrorPageProps>): JSX.Element {

    useTitle('K-Fleet Experience');

    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const indexedDBContext: IndexedDBContextProps = useContext<IndexedDBContextProps>(IndexedDBContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const systemContext: SystemContextProps = useContext<SystemContextProps>(SystemContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);

    const currentTheme: Theme = useTheme();

    const [showConfirmDisconnectDialog, setShowConfirmDisconnectDialog] = useState<boolean>(false);
    const [pending, setPending] = useState<boolean>(false);

    async function handleDisconnectPressed(): Promise<void> {

        if (userContext.currentUser !== null) { //if a user is logged in, there is a chance data will be lost when disconnecting - an extra confirm dialog is therefore necessary
            setShowConfirmDisconnectDialog(true);
        } else {

            setPending(true);

            await handleDisconnect(apiContext, modeContext, localStorageContext, indexedDBContext, userContext, tokenContext, systemContext);

            setPending(false);
        }
    }

    return (
        <div style={{
            textAlign: 'center',
            padding: '105px 5px 5px 5px',
        }}>
            <FontAwesomeIcon
                icon={faTriangleExclamation}
                size='5x'
                color={currentTheme.palette.error.main}
            />
            <br />
            <br />
            <br />
            <Typography style={{
                color: currentTheme.palette.text.primary,
            }}>
                {props.errorMessage}
            </Typography>
            <br />
            <br />
            {props.canDisconnect &&
                <LoadingButton 
                    width='150px'
                    height='40px'
                    text='Disconnect'
                    controller={pending}
                    onClick={handleDisconnectPressed}
                    buttonColor='error'
                    circularColor='primary'
                    circularSize='30px'
                    variant='contained'
                />
            }

            <ConfirmDialog 
                controller={showConfirmDisconnectDialog}
                onClose={async (confirm: boolean): Promise<void> => {
                    setShowConfirmDisconnectDialog(false);
                    if (confirm) {
                        await handleDisconnect(apiContext, modeContext, localStorageContext, indexedDBContext, userContext, tokenContext, systemContext);
                    }
                }}
                title='Possible Data Loss When Disconnecting'
                text='If you have unsynced data, it will be deleted upon disconnection. Are you sure you want to continue?'
                noText='No'
                yesText='Yes'
                loadingConfirm={false}
            />
        </div>
    );
}

export default ErrorPage;